import { createSlice } from '@reduxjs/toolkit';
import {
  searchTestResults,
  getTestResultDetail,
  updateTestResultNotes,
  updateValidationState,
} from '../../services/testHistoryApiService';

// Initial state
const initialState = {
  // Search results list
  searchResults: [],
  searchLoading: false,
  searchError: null,

  // Selected test result detail
  selectedResult: null,
  detailLoading: false,
  detailError: null,

  // Search filters (for persistence)
  lastSearchFilters: null,
  detailedResults: {}, // cache of detailed results by testResultId
};

// Slice
const testHistorySlice = createSlice({
  name: 'testHistory',
  initialState,
  reducers: {
    // Search actions
    setSearchLoading: (state, action) => {
      state.searchLoading = action.payload;
    },
    setSearchResults: (state, action) => {
      state.searchResults = action.payload;
      state.searchLoading = false;
      state.searchError = null;
    },
    setSearchError: (state, action) => {
      state.searchError = action.payload;
      state.searchLoading = false;
    },
    setLastSearchFilters: (state, action) => {
      state.lastSearchFilters = action.payload;
    },

    // Detail actions
    setDetailLoading: (state, action) => {
      state.detailLoading = action.payload;
    },
    setSelectedResult: (state, action) => {
      state.selectedResult = action.payload;
      state.detailLoading = false;
      state.detailError = null;
    },
    setDetailError: (state, action) => {
      state.detailError = action.payload;
      state.detailLoading = false;
    },

    // Clear actions
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchError = null;
      state.lastSearchFilters = null;
    },
    clearSelectedResult: (state) => {
      state.selectedResult = null;
      state.detailError = null;
    },
    setCachedDetail: (state, action) => {
      const { id, detail } = action.payload;
      state.detailedResults[id] = detail;
    },
    reset: (state) => {
      return initialState;
    },
    updateNotesLocally: (state, action) => {
      if (state.selectedResult) {
        state.selectedResult.notes = action.payload;
      }
    },
    updateValidationStateLocally: (state, action) => {
      if (state.selectedResult) {
        state.selectedResult.validation_state = action.payload.validation_state;

        state.selectedResult.total_events = action.payload.summary.total_events;

        state.selectedResult.passed_events = action.payload.summary.passed;

        state.selectedResult.failed_events = action.payload.summary.failed;

        state.selectedResult.missing_events = action.payload.summary.missing;

        state.selectedResult.non_validated_events = action.payload.summary.non_validated;

        state.selectedResult.not_present_in_app = action.payload.summary.not_present_in_app;

        state.selectedResult.pass_rate = parseFloat(action.payload.summary.pass_rate);
      }
    },
  },
});

// Export actions
export const {
  setSearchLoading,
  setSearchResults,
  setSearchError,
  setLastSearchFilters,
  setDetailLoading,
  setSelectedResult,
  setDetailError,
  clearSearchResults,
  clearSelectedResult,
  reset,
  setCachedDetail,
  updateNotesLocally,
  updateValidationStateLocally,
} = testHistorySlice.actions;

// Async thunk for searching test results
export const searchTestResultsAsync = (filters) => async (dispatch) => {
  dispatch(setSearchLoading(true));
  dispatch(setLastSearchFilters(filters));

  try {
    console.log('Searching test results with filters:', filters);

    const results = await searchTestResults(filters);

    console.log(`Found ${results.length} test results`);
    dispatch(setSearchResults(results));
  } catch (error) {
    console.error('Error searching test results:', error);
    dispatch(setSearchError(error.message));
  }
};

// Async thunk for fetching test result detail
export const fetchTestResultDetailAsync = (testResultId) => async (dispatch) => {
  dispatch(setDetailLoading(true));

  try {
    console.log(`Fetching detail for test result ID: ${testResultId}`);

    const detail = await getTestResultDetail(testResultId);

    console.log('Test result detail loaded');
    dispatch(setSelectedResult(detail));

    // Cache detail for export
    dispatch(setCachedDetail({ id: testResultId, detail }));
  } catch (error) {
    console.error('Error fetching test result detail:', error);
    dispatch(setDetailError(error.message));
  }
};

export const updateTestNotesAsync = (id, notes) => async (dispatch) => {
  try {
    console.log('Updating notes for test result:', id);

    await updateTestResultNotes(id, notes);

    // update redux immediately
    dispatch(updateNotesLocally(notes));
  } catch (error) {
    console.error('Error updating notes:', error);
    dispatch(setDetailError(error.message));
  }
};

export const updateValidationStateAsync = (id, validation_state, summary) => async (dispatch) => {
  try {
    await updateValidationState(id, validation_state, summary);

    dispatch(
      updateValidationStateLocally({
        validation_state,
        summary,
      }),
    );
  } catch (error) {
    console.error('Error updating validation state:', error);

    dispatch(setDetailError(error.message));
  }
};

// Selectors
export const selectSearchResults = (state) => state.testHistory.searchResults;
export const selectSearchLoading = (state) => state.testHistory.searchLoading;
export const selectSearchError = (state) => state.testHistory.searchError;
export const selectLastSearchFilters = (state) => state.testHistory.lastSearchFilters;
export const selectSelectedResult = (state) => state.testHistory.selectedResult;
export const selectDetailLoading = (state) => state.testHistory.detailLoading;
export const selectDetailError = (state) => state.testHistory.detailError;
export const selectCachedDetails = (state) => state.testHistory.detailedResults;

export default testHistorySlice.reducer;
